import { get, merge } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getVisibleItems, isVisible } from 'resolvers/getVisibleEntities';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc MemberDetailsHeaderWidget defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberDetailsHeaderWidgetService } from 'services/memberDetailsHeader';
 *
 * // create service instance and send/cancel request
 * const memberDetailsHeaderService = new MemberDetailsHeaderWidgetService();
 * const result = await memberDetailsHeaderService.fetchData( '<user-id>' ); // fetch data
 * memberDetailsHeaderService.cancel(); // cancel an active request
 */
export class MemberDetailsHeaderWidgetService extends HttpService {

    // make request and fetch data
    async fetchData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callerType
                    }
                } ),
            } );

            // extract component data
            const visibility = get( response, 'body.component.visibility', {} );
            const callerInfo = get( response, 'body.component.data.state.callerInfo', {} );
            const widgets = get( response, 'body.component.structure.widgets', [] );
            const quickActions = get( response, 'body.component.structure.quickActions', [] );
            const switchUserAction = get( response, 'body.component.structure.switchUserAction', [] );
            const savings = get( response, 'body.component.structure.savings', {} );
            const payBalance = get( response, 'body.component.structure.payBalance', {} );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    callerInfo,
                    widgets: getVisibleItems( widgets, visibility ),
                    quickActions: quickActions,
                    visibility,
                    switchUserAction: isVisible( switchUserAction, visibility ) ? switchUserAction : null,
                    savings,
                    payBalance
                }
            };
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make request to fetch actions data only
    async fetchActionsData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callerType
                    }
                } ),
            } );

            // extract component data
            const visibility = get( response, 'body.component.visibility', {} );
            const quickActions = get( response, 'body.component.structure.quickActions', [] );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    quickActions: quickActions,
                    visibility,
                }
            };
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // fetch live member status from ePost
    // member endpoint does not use callType
    async fetchLiveMemberStatus( { data } ) {
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: '/member/fetch-live-member-status',
                data: merge( {}, data, {
                    context: {
                        callId,
                        callerType,
                    },
                } ),
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: get( response, 'body', {} ),
            };
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                // HttpService throws HttpServiceError with { status: <httpCode> } on non-2xx responses
                httpStatus: get( errResponse, 'status', 0 ),
                data: null,
            };
        }
    }
}
